import { useEffect, useState } from 'react';
import { format, parseISO, differenceInDays, subDays } from 'date-fns';
import { Calendar, Footprints } from 'lucide-react';

import { db } from '@/services/storage/db';
import type { Hunt, HuntSit, HarvestRecord } from '@/types/hunt';
// M-09 (Defect Batch M): Dashboard "Log a Sit" CTA mounts the same sheet that
// the Hunt Journal page uses, so the user can log a sit in one tap from the
// Dashboard instead of two (Dashboard → Hunt Journal → Log a Sit).
import { LogSitSheet } from '@/components/hunts/LogSitSheet';
import { CollapsibleWidget } from './collapsible-widget';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ActiveHuntData {
  hunt: Hunt;
  sits: HuntSit[];
  harvests: HarvestRecord[];
  dayNumber: number;
  totalDays: number;
}

export interface ActiveHuntCardProps {
  seasonId: string;
  onHuntEnded?: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function findActiveHunt(hunts: Hunt[], today: string): Hunt | null {
  return (
    hunts.find((h) => h.startDate <= today && h.endDate >= today && !h.deletedAt) ?? null
  );
}

function buildSitSummary(sits: HuntSit[]): string {
  if (sits.length === 0) return 'Log your first sit';
  const morning = sits.filter((s) => s.period === 'morning').length;
  const evening = sits.filter((s) => s.period === 'evening').length;
  const allDay = sits.filter((s) => s.period === 'all-day').length;
  const parts: string[] = [`${sits.length} sit${sits.length !== 1 ? 's' : ''}`];
  if (morning > 0) parts.push(`${morning} morning`);
  if (evening > 0) parts.push(`${evening} evening`);
  if (allDay > 0) parts.push(`${allDay} all-day`);
  return parts.join(' · ');
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ActiveHuntCard({ seasonId, onHuntEnded }: ActiveHuntCardProps) {
  const [data, setData] = useState<ActiveHuntData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEnding, setIsEnding] = useState(false);
  const [endError, setEndError] = useState<string | null>(null);
  // M-09: open the Log Sit sheet inline from the Dashboard rather than
  // navigating to the Hunt Journal page first.
  const [showLogSitSheet, setShowLogSitSheet] = useState(false);

  useEffect(() => {
    let active = true;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- loading flag set before async fetch; reset in cleanup
    setIsLoading(true);

    async function load() {
      const today = format(new Date(), 'yyyy-MM-dd');
      const hunts = await db.getHuntsBySeason(seasonId);
      const activeHunt = findActiveHunt(hunts, today);

      if (!activeHunt) {
        if (active) {
          setData(null);
          setIsLoading(false);
        }
        return;
      }

      const [sits, harvests] = await Promise.all([
        db.getHuntSitsByHunt(activeHunt.id),
        db.getHarvestRecordsByHunt(activeHunt.id),
      ]);

      if (active) {
        const dayNumber =
          differenceInDays(parseISO(today), parseISO(activeHunt.startDate)) + 1;
        const totalDays =
          differenceInDays(parseISO(activeHunt.endDate), parseISO(activeHunt.startDate)) + 1;
        setData({ hunt: activeHunt, sits, harvests, dayNumber, totalDays });
        setIsLoading(false);
      }
    }

    load().catch((err) => {
      console.error('[ActiveHuntCard] load:', err);
      if (active) setIsLoading(false);
    });

    return () => {
      active = false;
    };
  }, [seasonId]);

  async function handleEndHunt() {
    if (!data) return;
    setIsEnding(true);
    setEndError(null);
    try {
      const yesterday = format(subDays(new Date(), 1), 'yyyy-MM-dd');
      await db.updateHunt(data.hunt.id, {
        endDate: yesterday,
        updatedAt: new Date().toISOString(),
      });
      setData(null);
      onHuntEnded?.();
    } catch (err) {
      console.error('[ActiveHuntCard] endHunt:', err);
      setEndError('Failed to end hunt. Please try again.');
    } finally {
      setIsEnding(false);
    }
  }

  if (isLoading || !data) return null;

  const { hunt, sits, harvests, dayNumber, totalDays } = data;
  const sitSummary = buildSitSummary(sits);
  const harvestCount = harvests.length;
  const collapsedSummary = `${hunt.name} — Day ${dayNumber} · ${sits.length} sit${sits.length !== 1 ? 's' : ''}`;

  return (
    <CollapsibleWidget
      id="active-hunt"
      title="ACTIVE HUNT"
      summary={collapsedSummary}
    >
      <div
        className="bg-[var(--color-surface)] border-x border-b border-[var(--color-border)] rounded-b-lg rounded-t-none p-4 flex flex-col gap-4"
        data-testid="active-hunt-card"
      >
        {/* Hunt name and day progress */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="font-display text-display-sm text-[var(--color-text-primary)] truncate">
              {hunt.name}
            </p>
            <p className="flex items-center gap-1 mt-0.5 text-body-sm text-[var(--color-text-muted)]">
              <Calendar size={13} aria-hidden />
              Day {dayNumber} of {totalDays}
            </p>
          </div>
          {harvestCount > 0 && (
            <span
              className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-body-xs font-medium bg-[var(--color-accent-subtle)] text-[var(--color-accent)]"
              data-testid="harvest-badge"
            >
              {harvestCount} harvest{harvestCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        {/* Sit summary */}
        <p className="flex items-center gap-1.5 text-body-sm text-[var(--color-text-secondary)]">
          <Footprints size={13} aria-hidden />
          {sitSummary}
        </p>

        {/* Inline error */}
        {endError && (
          <p className="text-body-sm text-[var(--color-accent)]" role="alert">
            {endError}
          </p>
        )}

        {/* Quick actions */}
        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={() => setShowLogSitSheet(true)}
            className="flex-1 inline-flex items-center justify-center px-3 py-2 rounded bg-[var(--color-accent)] hover:bg-[var(--color-accent-hover)] active:bg-[var(--color-accent-pressed)] text-[var(--color-text-display)] text-body-sm font-medium transition-colors"
            style={{ minHeight: '44px' }}
            data-testid="log-sit-button"
          >
            Log a Sit
          </button>
          <button
            type="button"
            onClick={() => {
              void handleEndHunt();
            }}
            disabled={isEnding}
            className="flex-1 inline-flex items-center justify-center px-3 py-2 rounded border border-[var(--color-border)] hover:border-[var(--color-accent)] text-[var(--color-text-secondary)] hover:text-[var(--color-accent)] text-body-sm font-medium transition-colors disabled:opacity-50"
            style={{ minHeight: '44px' }}
            data-testid="end-hunt-button"
          >
            {isEnding ? 'Ending…' : 'End Hunt'}
          </button>
        </div>
      </div>

      {/* M-09: Log Sit sheet mounts inside the active-hunt card so the user
          stays on the Dashboard when saving or cancelling the sit. */}
      {showLogSitSheet && (
        <LogSitSheet
          huntId={hunt.id}
          propertyId={hunt.propertyId}
          onClose={() => setShowLogSitSheet(false)}
        />
      )}
    </CollapsibleWidget>
  );
}
